/* eslint-disable react-hooks/exhaustive-deps */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AmplifyAlertBanner } from '../../../../components/AmplifyAlertBanner';
import { Table, Space, Input, Modal } from 'antd';
import { Button, Flex } from '@aws-amplify/ui-react';
import { EditOutlined } from '@mui/icons-material';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import vendorsService from '../../../../axios/vendorsService';
import CircleIcon from '@mui/icons-material/Circle';
import BlockIcon from '@mui/icons-material/Block';
import CMS from '../../../../cms/cms.json';
import { formatVendorData } from '../../../../utils/helpers';
import styles from './ManageVendors.module.css';
import '../../../../App.css';

const PAGE_SIZE = 8;

export const ManageVendors = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dataTable, setDataTable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({ variation: '', msg: '' });
  const [showConfirmationModal, setConfirmationModal] = useState(false);
  const [modalMsg, setModalMsg] = useState('');
  const [currentRow, setCurrentRow] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
    total: 0,
  });
  const [refreshKey, setRefreshKey] = useState(0);

  const searchDebounceRef = useRef(null);

  const columns = [
    {
      title: 'Vendor',
      align: 'center',
      dataIndex: 'vendorGroupName',
    },
    {
      title: 'Vendor Group Number',
      align: 'center',
      dataIndex: 'vendorGroupNumber',
    },
    {
      title: 'Subscription',
      align: 'center',
      dataIndex: 'subscriptionName',
    },
    {
      title: 'Status',
      align: 'center',
      dataIndex: 'status',
      render: (_, row) => (
        <Flex alignItems={'center'} justifyContent={'center'}>
          <CircleIcon
            className={styles['status-icon']}
            style={{ fill: row.status === 'active' ? 'green' : 'red' }}
          />
          <div>{row.status}</div>
        </Flex>
      ),
    },
    {
      title: 'Created',
      align: 'center',
      dataIndex: 'createdAt',
    },
    {
      title: 'Edit',
      align: 'center',
      key: 'action',
      render: (_, row) => (
        <Space>
          <Button
            size="small"
            variation="link"
            onClick={async () => {
              // eslint-disable-next-line no-unused-vars
              const { key, ...data } = row;
              const vendors = await vendorsService.getVendors();
              const currentVendorNames =
                vendors?.map((vendor) =>
                  String.prototype.toUpperCase.call(vendor.vendorGroupName),
                ) ?? [];
              navigate('/edit-vendor', {
                state: { ...data, currentVendorNames },
              });
            }}
          >
            <EditOutlined />
          </Button>
          {row.status === 'active' ? (
            <Button
              key="Deactivate User"
              title="Deactivate Vendor"
              variation="link"
              size="small"
              onClick={() => {
                setCurrentRow(row);
                setModalMsg('Do you want to deactivate this vendor?');
                setConfirmationModal(true);
              }}
            >
              <BlockIcon />
            </Button>
          ) : (
            <Button
              key="Activate User"
              title="Activate Vendor"
              variation="link"
              size="small"
              onClick={() => {
                setCurrentRow(row);
                setModalMsg('Do you want to activate this vendor?');
                setConfirmationModal(true);
              }}
            >
              <ManageAccountsIcon />
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const loadVendors = useCallback(async (page, pageSize, search) => {
    setLoading(true);
    try {
      const vendorsListRes = await vendorsService.getVendorsList(
        page,
        pageSize,
        search,
      );

      const items = formatVendorData(vendorsListRes?.data ?? []);

      setDataTable(items);
      setPagination({
        current: page,
        pageSize,
        total: vendorsListRes?.totalCount ?? 0,
      });
    } catch (err) {
      console.log('Error encountered when fetching vendors: ', err);
      setConfig({ variation: 'error', msg: 'Error fetching vendors' });
      setDataTable([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVendors(1, PAGE_SIZE, searchTerm);
  }, []);

  useEffect(() => {
    if (refreshKey === 0) {
      return;
    }
    loadVendors(pagination.current, pagination.pageSize, searchTerm);
  }, [refreshKey]);

  useEffect(() => {
    if (location.state) {
      const { variation, msg } = location.state;
      setConfig({ variation: variation, msg: msg });
    }
  }, [location.state]);

  const triggerRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleTableChange = (tablePagination) => {
    const page = tablePagination.current;
    const pageSize = tablePagination.pageSize;
    loadVendors(page, pageSize, searchTerm);
  };

  const handleSearchChange = (event) => {
    const value = event?.target?.value ?? '';
    setSearchTerm(value);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      loadVendors(1, pagination.pageSize, value);
    }, 300);
  };

  const handleSearch = (value) => {
    const searchValue = value ?? '';
    setSearchTerm(searchValue);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    loadVendors(1, pagination.pageSize, searchValue);
  };

  const deactivateVendor = async (row) => {
    setConfirmationModal(false);
    setLoading(true);
    let action = 'deactivate';
    await vendorsService
      .manageVendorStatus(row.vendorGroupNumber, action)
      .then((res) => {
        if (res?.data?.data?.manageVendorStatus) {
          triggerRefresh();
        } else {
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('Error deactivating vendor: ', err);
        setLoading(false);
        setConfig({
          variation: 'error',
          msg: 'Error deactivating vendor, please contact admin',
        });
      });
  };

  const activateVendor = async (row) => {
    setConfirmationModal(false);
    setLoading(true);
    let action = 'activate';
    await vendorsService
      .manageVendorStatus(row.vendorGroupNumber, action)
      .then((res) => {
        if (res?.data?.data?.manageVendorStatus) {
          triggerRefresh();
        } else {
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('Error activating vendor: ', err);
        setLoading(false);
        setConfig({
          variation: 'error',
          msg: 'Error activating vendor, please contact admin',
        });
      });
  };

  return (
    <div id="vendor-list-body">
      <AmplifyAlertBanner isDismissible={true} config={config} />
      <div className="manage-control">
        <h2 className="manage-vendors-title">
          {CMS.pages.find((o) => o.name === 'manageVendors')?.title}
        </h2>
      </div>

      <Input.Search
        className={styles['search-component']}
        placeholder="Search Vendors"
        allowClear
        value={searchTerm}
        onSearch={handleSearch}
        onChange={handleSearchChange}
      />

      <Table
        className={styles['data-table']}
        columns={columns}
        bordered
        size="small"
        dataSource={dataTable}
        pagination={{
          position: ['bottomLeft'],
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['8', '20', '50'],
        }}
        loading={loading}
        onChange={handleTableChange}
      />
      <Modal
        open={showConfirmationModal}
        onOk={() =>
          currentRow.status === 'active'
            ? deactivateVendor(currentRow)
            : activateVendor(currentRow)
        }
        onCancel={() => setConfirmationModal(false)}
      >
        <p>{modalMsg}</p>
      </Modal>
    </div>
  );
};
